// MenuItemSelector.jsx - kategori yönetimi eklendi
import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUndoAlt, FaSave, FaTimes, FaList, FaTags } from 'react-icons/fa';
import api from '../services/api.service';
import './MenuItemSelector.css';

const MenuItemSelector = ({ onMenuItemSelect }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Menü öğesi formu için state
  const [showItemForm, setShowItemForm] = useState(false);
  const [itemFormData, setItemFormData] = useState({
    id: null,
    name: '',
    description: '',
    price: '',
    categoryId: '',
    displayOrder: 0,
    isActive: true
  });
  const [itemFormMode, setItemFormMode] = useState('add'); // 'add' veya 'edit'
  
  // Kategori formu için state
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    id: null,
    name: '',
    description: '',
    displayOrder: 0,
    isActive: true
  });
  const [categoryFormMode, setCategoryFormMode] = useState('add'); // 'add' veya 'edit'
  
  // Kategori listesi görünümü için state
  const [showCategoryList, setShowCategoryList] = useState(false);
  
  // Verileri yükle
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesRes, menuItemsRes, recipesRes] = await Promise.all([
          api.getMenuCategories({ isActive: true }),
          api.getMenuItems({ isActive: true }),
          api.getRecipes()
        ]);
        
        setCategories(categoriesRes.data);
        setMenuItems(menuItemsRes.data);
        setRecipes(recipesRes.data);
        setError(null);
      } catch (err) {
        console.error("Veri yükleme hatası:", err);
        setError("Veriler yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Menü öğelerini filtrele
  const filteredMenuItems = menuItems.filter(item => {
    // Kategoriye göre filtrele
    if (selectedCategory && item.categoryId.toString() !== selectedCategory) {
      return false;
    }
    
    // Arama terimine göre filtrele
    if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });
  
  // Filtreleri sıfırla
  const resetFilters = () => {
    setSelectedCategory('');
    setSearchTerm('');
  };
  
  // Menü öğesinin reçetesi var mı kontrol et
  const hasRecipe = (menuItemId) => {
    return recipes.some(recipe => recipe.menuItem.id.toString() === menuItemId.toString());
  };
  
  // Menü öğesinin reçete ID'sini getir (varsa)
  const getRecipeId = (menuItemId) => {
    const recipe = recipes.find(r => r.menuItem.id.toString() === menuItemId.toString());
    return recipe ? recipe.id : null;
  };
  
  // Kategori formundaki değişiklikleri işle
  const handleCategoryFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryFormData({
      ...categoryFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Menü öğesi formundaki değişiklikleri işle
  const handleItemFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setItemFormData({
      ...itemFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Yeni kategori ekleme formunu göster
  const handleShowAddCategoryForm = () => {
    setCategoryFormData({
      id: null,
      name: '',
      description: '',
      displayOrder: 0,
      isActive: true
    });
    setCategoryFormMode('add');
    setShowCategoryForm(true);
  };
  
  // Kategori düzenleme formunu göster
  const handleShowEditCategoryForm = (category) => {
    setCategoryFormData({
      id: category.id,
      name: category.name,
      description: category.description || '',
      displayOrder: category.displayOrder || 0,
      isActive: category.isActive
    });
    setCategoryFormMode('edit');
    setShowCategoryForm(true);
  };
  
  // Kategori formunu kapat
  const handleCloseCategoryForm = () => {
    setShowCategoryForm(false);
  };
  
  // Kategori formunu gönder
  const handleSubmitCategoryForm = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Veriyi hazırla
      const submissionData = {
        ...categoryFormData,
        displayOrder: parseInt(categoryFormData.displayOrder)
      };
      
      let response;
      
      if (categoryFormMode === 'add') {
        response = await api.createMenuCategory(submissionData);
        setCategories([...categories, response.data]);
      } else {
        response = await api.updateMenuCategory(categoryFormData.id, submissionData);
        
        // Kategoriler listesini güncelle
        setCategories(categories.map(category => 
          category.id === response.data.id ? response.data : category
        ));
      }
      
      // Formu kapat
      setShowCategoryForm(false);
      setError(null);
    } catch (err) {
      console.error("Kategori kaydetme hatası:", err);
      setError("Kategori kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };
  
  // Kategori sil
  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.deleteMenuCategory(id);
      
      // Kategoriler listesini güncelle
      setCategories(categories.filter(category => category.id !== id));
      setError(null);
    } catch (err) {
      console.error("Kategori silme hatası:", err);
      setError("Kategori silinirken bir hata oluştu. Bu kategoride menü öğeleri olabilir.");
    } finally {
      setLoading(false);
    }
  };
  
  // Yeni menü öğesi ekleme formunu göster
  const handleShowAddItemForm = () => {
    setItemFormData({
      id: null,
      name: '',
      description: '',
      price: '',
      categoryId: categories.length > 0 ? categories[0].id.toString() : '',
      displayOrder: 0,
      isActive: true
    });
    setItemFormMode('add');
    setShowItemForm(true);
  };
  
  // Menü öğesi düzenleme formunu göster
  const handleShowEditItemForm = (item) => {
    setItemFormData({
      id: item.id,
      name: item.name,
      description: item.description || '',
      price: item.price,
      categoryId: item.categoryId.toString(),
      displayOrder: item.displayOrder || 0,
      isActive: item.isActive
    });
    setItemFormMode('edit');
    setShowItemForm(true);
  };
  
  // Menü öğesi formunu kapat
  const handleCloseItemForm = () => {
    setShowItemForm(false);
  };
  
  // Menü öğesi formunu gönder
  const handleSubmitItemForm = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Veriyi hazırla
      const submissionData = {
        ...itemFormData,
        price: parseFloat(itemFormData.price),
        displayOrder: parseInt(itemFormData.displayOrder)
      };
      
      let response;
      
      if (itemFormMode === 'add') {
        response = await api.createMenuItem(submissionData);
        setMenuItems([...menuItems, response.data]);
      } else {
        response = await api.updateMenuItem(itemFormData.id, submissionData);
        
        // Menü öğeleri listesini güncelle
        setMenuItems(menuItems.map(item => 
          item.id === response.data.id ? response.data : item
        ));
      }
      
      // Formu kapat
      setShowItemForm(false);
      setError(null);
    } catch (err) {
      console.error("Menü öğesi kaydetme hatası:", err);
      setError("Menü öğesi kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };
  
  // Menü öğesi sil
  const handleDeleteMenuItem = async (id) => {
    if (!window.confirm('Bu menü öğesini silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.deleteMenuItem(id);
      
      // Menü öğeleri listesini güncelle
      setMenuItems(menuItems.filter(item => item.id !== id));
      setError(null);
    } catch (err) {
      console.error("Menü öğesi silme hatası:", err);
      setError("Menü öğesi silinirken bir hata oluştu. Öğenin reçetesi olabilir.");
    } finally {
      setLoading(false);
    }
  };
  
  // Kategori düğmesinin tıklama durumunu değiştir
  const toggleCategoryList = () => {
    setShowCategoryList(!showCategoryList);
  };
  
  return (
    <div className="menu-item-selector">
      {error && <div className="error-message">{error}</div>}
      
      <div className="actions-bar">
        <div className="filter-container">
          <div className="filter-group">
            <label className="filter-label">Kategori Filtrele</label>
            <select
              className="filter-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={loading}
            >
              <option value="">Tüm Kategoriler</option>
              {categories.map(category => (
                <option key={category.id} value={category.id.toString()}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Menü Öğesi Ara</label>
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="İsme göre ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          
          <button 
            className="reset-button"
            onClick={resetFilters}
            disabled={(!selectedCategory && !searchTerm) || loading}
          >
            <FaUndoAlt className="button-icon" />
            Temizle
          </button>
        </div>
        
        <div className="action-buttons">
          <button 
            className={`category-button ${showCategoryList ? 'active' : ''}`}
            onClick={toggleCategoryList}
            disabled={loading}
          >
            <FaTags className="button-icon" />
            Kategoriler
          </button>
          
          <button 
            className="add-button"
            onClick={handleShowAddItemForm}
            disabled={loading}
          >
            <FaPlus className="button-icon" />
            Yeni Menü Öğesi
          </button>
        </div>
      </div>
      
      {/* Kategori Listesi */}
      {showCategoryList && (
        <div className="category-list-container">
          <div className="category-list-header">
            <h3>Kategoriler</h3>
            <button 
              className="add-category-button"
              onClick={handleShowAddCategoryForm}
              disabled={loading}
            >
              <FaPlus className="button-icon" />
              Yeni Kategori
            </button>
          </div>
          
          {categories.length > 0 ? (
            <div className="category-list">
              {categories.map(category => (
                <div key={category.id} className="category-item">
                  <div className="category-item-content">
                    <h4 className="category-name">{category.name}</h4>
                    {category.description && (
                      <p className="category-description">{category.description}</p>
                    )}
                    <div className="category-meta">
                      <span className="category-display-order">Sıra: {category.displayOrder}</span>
                      <span className={`category-status ${category.isActive ? 'active' : 'inactive'}`}>
                        {category.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                  </div>
                  <div className="category-item-actions">
                    <button 
                      className="edit-button"
                      onClick={() => handleShowEditCategoryForm(category)}
                      disabled={loading}
                    >
                      <FaEdit className="button-icon" />
                      Düzenle
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteCategory(category.id)}
                      disabled={loading}
                    >
                      <FaTrash className="button-icon" />
                      Sil
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="info-message">
              Henüz kategori eklenmemiş. "Yeni Kategori" butonu ile kategori ekleyebilirsiniz.
            </div>
          )}
        </div>
      )}
      
      {/* Kategori Formu */}
      {showCategoryForm && (
        <div className="form-container">
          <form onSubmit={handleSubmitCategoryForm} className="form">
            <div className="form-header">
              <h3>{categoryFormMode === 'add' ? 'Yeni Kategori' : 'Kategori Düzenle'}</h3>
              <button 
                type="button" 
                className="close-button"
                onClick={handleCloseCategoryForm}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="form-body">
              <div className="form-group">
                <label htmlFor="category-name">Kategori Adı</label>
                <input
                  type="text"
                  id="category-name"
                  name="name"
                  value={categoryFormData.name}
                  onChange={handleCategoryFormChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="category-description">Açıklama</label>
                <textarea
                  id="category-description"
                  name="description"
                  value={categoryFormData.description}
                  onChange={handleCategoryFormChange}
                  disabled={loading}
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category-display-order">Görüntüleme Sırası</label>
                  <input
                    type="number"
                    id="category-display-order"
                    name="displayOrder"
                    value={categoryFormData.displayOrder}
                    onChange={handleCategoryFormChange}
                    min="0"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={categoryFormData.isActive}
                      onChange={handleCategoryFormChange}
                      disabled={loading}
                    />
                    Aktif
                  </label>
                </div>
              </div>
            </div>
            
            <div className="form-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCloseCategoryForm}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="submit"
                className="save-button"
                disabled={loading}
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Menü Öğesi Formu */}
      {showItemForm && (
        <div className="form-container">
          <form onSubmit={handleSubmitItemForm} className="form">
            <div className="form-header">
              <h3>{itemFormMode === 'add' ? 'Yeni Menü Öğesi' : 'Menü Öğesi Düzenle'}</h3>
              <button 
                type="button" 
                className="close-button"
                onClick={handleCloseItemForm}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="form-body">
              <div className="form-group">
                <label htmlFor="item-name">Öğe Adı</label>
                <input
                  type="text"
                  id="item-name"
                  name="name"
                  value={itemFormData.name}
                  onChange={handleItemFormChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="item-description">Açıklama</label>
                <textarea
                  id="item-description"
                  name="description"
                  value={itemFormData.description}
                  onChange={handleItemFormChange}
                  disabled={loading}
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="item-price">Fiyat (₺)</label>
                  <input
                    type="number"
                    id="item-price"
                    name="price"
                    value={itemFormData.price}
                    onChange={handleItemFormChange}
                    step="0.01"
                    min="0"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="item-category">Kategori</label>
                  <select
                    id="item-category"
                    name="categoryId"
                    value={itemFormData.categoryId}
                    onChange={handleItemFormChange}
                    required
                    disabled={loading || categories.length === 0}
                  >
                    {categories.length === 0 ? (
                      <option value="">Önce kategori ekleyin</option>
                    ) : (
                      categories.map(category => (
                        <option key={category.id} value={category.id.toString()}>
                          {category.name}
                        </option>
                      ))
                    )}
                  </select>
                  {categories.length === 0 && (
                    <p className="form-help-text">
                      Önce bir kategori eklemelisiniz.
                      <button 
                        type="button" 
                        className="inline-button"
                        onClick={() => {
                          setShowItemForm(false);
                          handleShowAddCategoryForm();
                        }}
                      >
                        Kategori Ekle
                      </button>
                    </p>
                  )}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="item-display-order">Görüntüleme Sırası</label>
                  <input
                    type="number"
                    id="item-display-order"
                    name="displayOrder"
                    value={itemFormData.displayOrder}
                    onChange={handleItemFormChange}
                    min="0"
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={itemFormData.isActive}
                      onChange={handleItemFormChange}
                      disabled={loading}
                    />
                    Aktif
                  </label>
                </div>
              </div>
            </div>
            
            <div className="form-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCloseItemForm}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="submit"
                className="save-button"
                disabled={loading || categories.length === 0}
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {loading && !showItemForm && !showCategoryForm ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Veriler yükleniyor...</p>
        </div>
      ) : filteredMenuItems.length > 0 ? (
        <div className="menu-item-grid">
          {filteredMenuItems.map(item => {
            const itemHasRecipe = hasRecipe(item.id);
            const recipeId = getRecipeId(item.id);
            const categoryName = categories.find(c => c.id.toString() === item.categoryId?.toString())?.name || 'Kategorisiz';
            
            return (
              <div className="menu-item-card" key={item.id}>
                {item.imageUrl && (
                  <div className="menu-item-image-container">
                    <img 
                      src={item.imageUrl} 
                      alt={item.name} 
                      className="menu-item-image" 
                    />
                  </div>
                )}
                <div className="menu-item-content">
                  <h3 className="menu-item-title">{item.name}</h3>
                  <div className="menu-item-badges">
                    <span className="category-badge">
                      {categoryName}
                    </span>
                    <span className="price-badge">₺{parseFloat(item.price).toFixed(2)}</span>
                    {!item.isActive && <span className="inactive-badge">Pasif</span>}
                  </div>
                  {item.description && (
                    <p className="menu-item-description">
                      {item.description.length > 80 
                        ? `${item.description.substring(0, 80)}...` 
                        : item.description
                      }
                    </p>
                  )}
                </div>
                <div className="menu-item-footer">
                  <div className="menu-item-actions">
                    <button 
                      className="edit-button"
                      onClick={() => handleShowEditItemForm(item)}
                      disabled={loading}
                    >
                      <FaEdit className="button-icon" />
                      Düzenle
                    </button>
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteMenuItem(item.id)}
                      disabled={loading || itemHasRecipe}
                      title={itemHasRecipe ? "Reçetesi olduğu için silinemez" : "Sil"}
                    >
                      <FaTrash className="button-icon" />
                      Sil
                    </button>
                  </div>
                  <button 
                    className={`recipe-button ${itemHasRecipe ? 'warning' : 'primary'}`}
                    onClick={() => onMenuItemSelect(itemHasRecipe ? recipeId : item.id, itemHasRecipe)}
                  >
                    <FaPlus className="button-icon" />
                    {itemHasRecipe 
                      ? "Reçete Düzenle" 
                      : "Reçete Oluştur"
                    }
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="info-message">
          {searchTerm || selectedCategory 
            ? 'Seçilen filtreye uygun menü öğesi bulunamadı.' 
            : categories.length === 0 
              ? 'Önce kategori eklemeniz gerekiyor. "Kategoriler" butonuna tıklayarak kategori ekleyebilirsiniz.' 
              : 'Henüz menü öğesi eklenmemiş. "Yeni Menü Öğesi" butonu ile öğe ekleyebilirsiniz.'
          }
        </div>
      )}
    </div>
  );
};

export default MenuItemSelector;